/**
 * Every word the interface shows for a value of the API: states, phases, connections.
 *
 * Kept in one place so that the badge, the list and the overview say the same thing for the
 * same state. `tone` picks the colour of a badge (styles in components/plane-badge.js): `ok`,
 * `progress`, `warning`, `danger` or `muted`.
 */

/** The displayed state of an instance (backend: DisplayedState), in order of urgency. */
export const STATE = {
  rollback_failed: {
    label: 'Rollback failed',
    tone: 'danger',
    hint: 'The apply failed and the workspace could not be brought back to a safe state: an operator must intervene.',
  },
  failed: {
    label: 'Failed',
    tone: 'danger',
    // the generic text: stateHint says which of the failures it is
    hint: 'The last apply or the container of the workspace failed: see the error.',
  },
  stuck: {
    label: 'Stuck',
    tone: 'warning',
    hint: 'The server acknowledged the change more than 10 minutes ago and has not reported an outcome. The operator has been notified.',
  },
  stopped: {
    label: 'Stopped',
    tone: 'danger',
    hint: 'The container was stopped outside the plane: nothing runs. Restart the workspace to start it again.',
  },
  server_offline: {
    label: 'Server offline',
    tone: 'muted',
    hint: 'The server is not connected. The desired revision is saved: it will be applied when the server reconnects.',
  },
  running_degraded: {
    label: 'Degraded',
    tone: 'warning',
    hint: 'The workspace runs, but a channel or the model provider does not answer. The revision is active: check the channel token and the model.',
  },
  updating: {
    label: 'Updating',
    tone: 'progress',
    hint: 'The desired revision is not applied yet: waiting for the server to report.',
  },
  running: {
    label: 'Running',
    tone: 'ok',
    hint: 'The desired revision is applied and the workspace answers.',
  },
  unknown: {
    label: 'Unknown',
    tone: 'muted',
    hint: 'The server has not reported anything about this workspace yet.',
  },
};

/** The states that need somebody to look at them, shown first on the overview. */
export const ATTENTION_STATES = ['rollback_failed', 'failed', 'stuck', 'stopped', 'server_offline', 'running_degraded'];

export const stateOf = (code) => STATE[code] ?? STATE.unknown;

/**
 * The explanation of the state of an instance. A failure says what runs after it, from the revisions
 * the clawlet reported: `failed` covers a first apply that left nothing running, a new revision
 * refused or rolled back while the previous one stays, and a container that stopped after its apply.
 */
export const stateHint = (instance) => {
  if (instance.state !== 'failed') {
    return stateOf(instance.state).hint;
  }
  const { desiredRevision: desired, appliedRevision: applied } = instance;
  if (!applied) {
    return 'The desired revision could not be applied and no revision is installed: nothing runs.';
  }
  if (desired && applied.id === desired.id) {
    return 'The desired revision is installed, but its container is not running as it should.';
  }
  const previous = applied.number == null ? 'the previous revision' : `revision ${applied.number}`;
  if (instance.observedState === 'rolled_back') {
    return `The desired revision did not start: the workspace was rolled back and ${previous} is running.`;
  }
  return `The desired revision could not be applied: ${previous} is still installed.`;
};

/** How urgent a state is: 0 is the most urgent. Lists sort by it. */
export const urgency = (code) => {
  const index = Object.keys(STATE).indexOf(code);
  return index < 0 ? Object.keys(STATE).length : index;
};

export const CONNECTION = {
  online: { label: 'Online', tone: 'ok' },
  offline: { label: 'Offline', tone: 'danger' },
  never: { label: 'Never connected', tone: 'muted' },
};

export const SERVER_STATUS = {
  active: { label: 'Active', tone: 'ok' },
  revoked: { label: 'Revoked', tone: 'danger' },
};

/** The phases of an apply, in the order the clawlet runs them; rollback only after a failure. */
export const PHASE = {
  render: 'Render configuration',
  validate: 'Validate',
  secrets: 'Resolve secrets',
  write: 'Write files',
  reload: 'Restart container',
  health: 'Health check',
  done: 'Done',
  rollback: 'Roll back',
};

export const PHASE_STATUS = {
  started: { label: 'started', tone: 'progress' },
  ok: { label: 'ok', tone: 'ok' },
  failed: { label: 'failed', tone: 'danger' },
};

export const REVISION_STATUS = {
  draft: { label: 'Draft', tone: 'muted' },
  published: { label: 'Published', tone: 'ok' },
};

export const HEALTH = {
  starting: 'Starting',
  ready: 'Ready',
  degraded: 'Degraded',
};

/** The operator's rules at the head of AGENTS.md: written by operators, read by customers. */
export const OPERATOR_RULES = {
  title: 'Operator rules',
  description: 'At the head of AGENTS.md, before the rules of the customer, who reads them but cannot change them. Empty for none.',
};

/** The three persona files, in the order the editor shows them. */
export const PERSONA = [
  { key: 'soul', file: 'SOUL.md', description: 'Who the agent is: character, voice, what it cares about.' },
  { key: 'agents', file: 'AGENTS.md', description: 'How the agent works: rules, limits, what to do and not to do.' },
  { key: 'user', file: 'USER.md', description: 'Who the agent talks to: names, preferences, context.' },
];
